import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState, type FormEvent } from "react";
import { api, ApiError } from "../api";
import { refreshCrew } from "../crew/refreshCrew";
import { DialogLayer } from "../ui/Dialog";
import { countText, recipientsText } from "./recipients";
import { dayLabel, REQUESTS_KEY } from "./types";
import "./requests.css";

function errorText(error: unknown): string {
  if (!(error instanceof ApiError)) return "Nie udało się wysłać zapytania";
  const details = Array.isArray(error.details)
    ? (error.details as { message?: unknown }[])
        .map((d) => d.message)
        .filter((m): m is string => typeof m === "string")
    : [];
  return details.length > 0 ? details.join(". ") : error.message;
}

type Sent = {
  sent: Array<{ personId: string; name?: string }>;
  noContact: Array<{ personId: string; name?: string }>;
};
const names = (list: { name?: string | undefined }[]) =>
  list.every((p) => p.name)
    ? list.map((p) => p.name).join(", ")
    : countText(list.length);
type Named = { personId: string; name?: string };
/** '2026-10-31' → '31.10'. */
const dayMonth = (day: string) =>
  `${Number(day.slice(8, 10))}.${Number(day.slice(5, 7))}`;

/** An event to attach (the grid's question): those of the chosen days the user may see. */
export type EventChoice = { id: string; name: string };

/**
 * Stage 2: a question about days, sent by hand — „Wyślij zapytanie” in the grid (the selected people and days, an
 * event optional) and „Poproś o dostępność” (one person „Niedostępny”, the event, its function or place). The person
 * answers „tak” / „nie” per day from the link; „tak” for every day of a question about an event adds them to its crew.
 * Owner 2026-10-27 12:26 / review M3: before „Wyślij” the server's list — „Wyślę do: …”, whoever has no contact apart
 * („Brak kontaktu: …”); the send carries it (review M2: changed meanwhile — 409, read again). Review I1: `perPerson` —
 * several people from the grid's „Obsadź”, each asked only about their own days: one question each, one „Wyślij”.
 */
export function DateQuestionDialog({
  title,
  people: givenPeople = [],
  days: givenDays = [],
  perPerson,
  chosenDays,
  event,
  events,
  functionId,
  positionId,
  initialMessage,
  inCrew = false,
  onClose,
  onSent,
}: {
  title: string;
  /** `name` absent: hidden from the role (rule 7a) — the dialog says how many. */
  people?: Array<{ personId: string; name?: string | undefined }>;
  /** The days that may be asked; `chosenDays` ticked at first (default: all). */
  days?: string[];
  /** Review I1: one question per person, with their own days (the days are fixed then). */
  perPerson?: Array<{
    personId: string;
    name?: string | undefined;
    days: string[];
  }>;
  chosenDays?: string[];
  /** A fixed event („Poproś o dostępność”). */
  event?: EventChoice;
  /** Events to attach (the grid), none = no choice. */
  events?: EventChoice[];
  functionId?: string;
  positionId?: string;
  initialMessage?: string;
  /** The people are already in the event's crew (the grid's event panel): „tak” only marks them „Potwierdzony”. */
  inCrew?: boolean;
  onClose: () => void;
  onSent?: () => void;
}) {
  const queryClient = useQueryClient();
  const people = perPerson ?? givenPeople;
  const days = perPerson
    ? [...new Set(perPerson.flatMap((p) => p.days))].sort()
    : givenDays;
  const [chosen, setChosen] = useState<Set<string>>(
    () => new Set(chosenDays ?? days),
  );
  const ids = people.map((p) => p.personId);
  const preview = useQuery({
    queryKey: [...REQUESTS_KEY, "dates-to", ids],
    staleTime: 0,
    refetchOnMount: "always",
    enabled: ids.length > 0,
    queryFn: () =>
      api<{ recipients?: Named[]; noContact?: Named[] }>(
        "/api/requests/dates/recipients",
        { method: "POST", body: { personIds: ids } },
      ),
  });
  // without the server's list (it failed, or an old server): the people given
  const reach = preview.data?.recipients;
  const reachIds = new Set((reach ?? people).map((p) => p.personId));
  const noContact = preview.data?.noContact ?? [];
  const [eventId, setEventId] = useState(event?.id ?? "");
  const [message, setMessage] = useState(
    initialMessage ??
      (event
        ? `Czy możesz pracować przy wydarzeniu „${event.name}” w te dni?`
        : "Czy możesz pracować w te dni?"),
  );
  const extra = {
    message: message.trim(),
    ...(eventId ? { eventId } : {}),
    ...(functionId ? { functionId } : {}),
    ...(positionId ? { positionId } : {}),
  };
  // review I1 (panel A): the people sent so far — a send that failed partway is retried only for the rest
  const done = useRef<Sent>({ sent: [], noContact: [] });
  const [sentSoFar, setSentSoFar] = useState<Named[]>([]);
  const send = useMutation({
    mutationFn: async (): Promise<Sent> => {
      if (!perPerson) {
        return api<Sent>("/api/requests/dates", {
          method: "POST",
          body: {
            personIds: ids,
            days: days.filter((d) => chosen.has(d)),
            ...extra,
            ...(reach ? { expected: reach.map((r) => r.personId) } : {}),
          },
        });
      }
      // review I1: one question per person, their own days only (no contact: not sent — listed apart)
      const out = done.current;
      const already = new Set([...out.sent, ...out.noContact].map((x) => x.personId));
      for (const p of perPerson.filter((x) => reachIds.has(x.personId) && !already.has(x.personId))) {
        const r = await api<Sent>("/api/requests/dates", {
          method: "POST",
          body: {
            personIds: [p.personId],
            days: p.days,
            ...extra,
            ...(reach ? { expected: [p.personId] } : {}),
          },
        });
        out.sent.push(...r.sent);
        out.noContact.push(...r.noContact);
        setSentSoFar([...out.sent]);
      }
      return { sent: [...out.sent], noContact: [...out.noContact] };
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: REQUESTS_KEY });
      await refreshCrew(queryClient);
      onSent?.();
    },
    onError: async (e) => {
      // some went before the error: their rows show „zapytany” — the crew and the questions read again
      if (done.current.sent.length > 0) {
        await queryClient.invalidateQueries({ queryKey: REQUESTS_KEY });
        await refreshCrew(queryClient);
      }
      if (e instanceof ApiError && e.status === 409) await preview.refetch();
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    send.mutate();
  };
  const result = send.data;
  if (result) {
    return (
      <div className="dialog-backdrop">
        <div
          className="dialog panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby="date-question-sent"
          data-feature-id="requests.dates"
        >
          <DialogLayer onClose={onClose} />
          <h2 id="date-question-sent">
            {result.sent.length > 0 ? "Zapytanie wysłane" : "Nic nie wysłano"}
          </h2>
          {result.sent.length > 0 && <p>Wysłano do: {names(result.sent)}.</p>}
          {result.noContact.length > 0 && (
            <p className="alert" role="alert">
              Bez konta, e-maila i telefonu — nie da się zapytać:{" "}
              {names(result.noContact)}. Uzupełnij kontakt w Osoby.
            </p>
          )}
          <div className="actions">
            <button
              className="btn primary"
              type="button"
              onClick={onClose}
              autoFocus
            >
              Zamknij
            </button>
          </div>
        </div>
      </div>
    );
  }
  const attached = event ?? events?.find((e) => e.id === eventId);
  return (
    <div className="dialog-backdrop">
      <form
        className="dialog panel request-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="date-question-title"
        onSubmit={submit}
        data-feature-id="requests.dates"
      >
        <DialogLayer onClose={onClose} busy={send.isPending} />
        <h2 id="date-question-title">{title}</h2>
        {/* owner 2026-10-27 12:26: to whom, before „Wyślij” */}
        <p className="recipients">
          <strong>
            {preview.isLoading
              ? "Sprawdzam, do kogo…"
              : perPerson && perPerson.every((p) => p.name)
                ? `Wyślę do: ${perPerson
                    .filter((p) => reachIds.has(p.personId))
                    .map((p) => `${p.name}: ${p.days.map(dayMonth).join(", ")}`)
                    .join(" · ")}`
                : recipientsText(reach ?? people)}
          </strong>
        </p>
        {noContact.length > 0 && (
          <p className="muted">Brak kontaktu: {names(noContact)}</p>
        )}
        {preview.error && (
          <p className="alert" role="alert">
            {preview.error instanceof ApiError
              ? preview.error.message
              : "Nie udało się sprawdzić, do kogo pójdzie zapytanie"}
          </p>
        )}
        {!perPerson && (
          <fieldset className="date-question-days">
            <legend>Dni</legend>
            {days.map((d) => (
              <label key={d} className="check">
                <input
                  type="checkbox"
                  checked={chosen.has(d)}
                  onChange={(e) => {
                    const next = new Set(chosen);
                    if (e.target.checked) next.add(d);
                    else next.delete(d);
                    setChosen(next);
                  }}
                />
                <span>{dayLabel(d)}</span>
              </label>
            ))}
          </fieldset>
        )}
        {event ? (
          <p className="muted">Wydarzenie: {event.name}</p>
        ) : (
          events &&
          events.length > 0 && (
            <label className="field">
              <span>Wydarzenie (nieobowiązkowe)</span>
              <select
                value={eventId}
                onChange={(e) => setEventId(e.target.value)}
              >
                <option value="">— bez wydarzenia —</option>
                {events.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.name}
                  </option>
                ))}
              </select>
            </label>
          )
        )}
        <label className="field">
          <span>Treść zapytania</span>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={3}
            maxLength={2000}
            required
          />
        </label>
        <p className="muted">
          Osoba dostanie link (dzwonek, e-mail albo SMS) i odpowie „tak” albo
          „nie” dla każdego dnia — bez logowania. Odpowiedzi zapiszą jej
          dostępność.
          {attached && inCrew
            ? ` Gdy odpowie „tak” na wszystkie dni, zostanie oznaczona w obsadzie „${attached.name}” jako „Potwierdzony” (z oznaczeniem „dostępność potwierdzona”).`
            : attached
            ? ` Gdy odpowie „tak” na wszystkie dni, trafi na wolne miejsce swojej funkcji w „${attached.name}” w te dni, jako „Potwierdzony” z oznaczeniem „dostępność potwierdzona”; bez wolnego miejsca — „potwierdził — przydziel ręcznie”.`
            : ""}
        </p>
        {perPerson && sentSoFar.length > 0 && send.error && (
          <p className="alert">
            Wysłano do: {names(sentSoFar)} · Nie wysłano do:{" "}
            {names(perPerson.filter((p) => reachIds.has(p.personId) && !sentSoFar.some((x) => x.personId === p.personId)))} (błąd)
          </p>
        )}
        {send.error && (
          <p className="alert" role="alert">
            {send.error instanceof ApiError && send.error.status === 409
              ? "Lista adresatów się zmieniła — sprawdź ją i wyślij ponownie."
              : errorText(send.error)}
          </p>
        )}
        <div className="actions">
          <button
            className="btn primary"
            type="submit"
            disabled={
              send.isPending ||
              chosen.size === 0 ||
              message.trim() === "" ||
              preview.isFetching ||
              (!!reach && reach.length === 0)
            }
          >
            {send.isPending ? "Wysyłanie…" : "Wyślij zapytanie"}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Anuluj
          </button>
        </div>
      </form>
    </div>
  );
}
